import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConnectivityProvider, useConnectivity } from "./context/ConnectivityContext";
import { AudioProvider } from "./context/AudioContext";
import OfflineScreen from "./components/OfflineScreen";
import ConnectivityIndicator from "./components/ConnectivityIndicator";
import UpdatePrompt from "./components/UpdatePrompt";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Legal from "./pages/Legal";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import PlayFlow from "./pages/PlayFlow";
import Daily from "./pages/Daily";
import Rivals from "./pages/Rivals";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import Cards from "./pages/Cards";
import Journey from "./pages/Journey";
import Seasons from "./pages/Seasons";
import MySeasons from "./pages/MySeasons";
import Prestige from "./pages/Prestige";
import HallOfRenown from "./pages/HallOfRenown";
import Founder from "./pages/Founder";
import FounderAnalytics from "./pages/FounderAnalytics";
import FounderReports from "./pages/FounderReports";
import FounderSystemHealth from "./pages/FounderSystemHealth";
import FounderAudioStatus from "./pages/FounderAudioStatus";
import FounderBeta from "./pages/FounderBeta";
import AdminAudio from "./pages/AdminAudio";
import Sponsors from "./pages/Sponsors";
import Earn from "./pages/Earn";
import CommissionedArchitecture from "./pages/CommissionedArchitecture";
import Mission from "./pages/Mission";
import History from "./pages/History";
import HistoryDetail from "./pages/HistoryDetail";
import RecoveryCamp from "./pages/RecoveryCamp";
import Achievements from "./pages/Achievements";
import Journal from "./pages/Journal";
import Leaderboards from "./pages/Leaderboards";
import PublicProfile from "./pages/PublicProfile";
import Admin from "./pages/Admin";
import Splash from "./components/Splash";

function Loader() {
  return <Splash />;
}

function Protected({ children, adminOnly, founderOnly }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (founderOnly && user.role !== "founder") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function FounderGate({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "founder") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ConnectivityProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              style: { background: "#1A2C21", color: "#F2E8D3", border: "1px solid rgba(181,166,66,0.4)" },
            }}
          />
          <ConnectivityGate>
            <AudioProvider>
            <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/leaderboards" element={<Leaderboards />} />
          <Route path="/sponsors" element={<Sponsors />} />
          <Route path="/seasons" element={<Seasons />} />
          <Route path="/seasons/:seasonId" element={<Seasons detail />} />
          <Route path="/hall-of-renown" element={<HallOfRenown />} />
          <Route path="/explorer/:userId" element={<PublicProfile />} />
          <Route path="/login" element={<PublicOnly><Auth /></PublicOnly>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/play" element={<Protected><PlayFlow /></Protected>} />
          <Route path="/daily" element={<Protected><Daily /></Protected>} />
          <Route path="/rivals" element={<Protected><Rivals /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/cards" element={<Protected><Cards /></Protected>} />
          <Route path="/earn" element={<Protected><Earn /></Protected>} />
          <Route path="/journey" element={<Protected><Journey /></Protected>} />
          <Route path="/my-seasons" element={<Protected><MySeasons /></Protected>} />
          <Route path="/prestige" element={<Protected><Prestige /></Protected>} />
          <Route path="/admin/commissioned-expeditions" element={<Protected adminOnly><CommissionedArchitecture /></Protected>} />
          <Route path="/mission/:sessionId" element={<Protected><Mission /></Protected>} />
          <Route path="/history" element={<Protected><History /></Protected>} />
          <Route path="/history/:sessionId" element={<Protected><HistoryDetail /></Protected>} />
          <Route path="/camp" element={<Protected><RecoveryCamp /></Protected>} />
          <Route path="/achievements" element={<Protected><Achievements /></Protected>} />
          <Route path="/journal" element={<Protected><Journal /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
          <Route path="/admin/audio" element={<Protected adminOnly><AdminAudio /></Protected>} />
          <Route path="/founder" element={<FounderGate><Founder /></FounderGate>} />
          <Route path="/founder/analytics" element={<FounderGate><FounderAnalytics /></FounderGate>} />
          <Route path="/founder/reports" element={<FounderGate><FounderReports /></FounderGate>} />
          <Route path="/founder/system-health" element={<FounderGate><FounderSystemHealth /></FounderGate>} />
          <Route path="/founder/audio" element={<FounderGate><FounderAudioStatus /></FounderGate>} />
          <Route path="/founder/beta" element={<FounderGate><FounderBeta /></FounderGate>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </AudioProvider>
          </ConnectivityGate>
          <ConnectivityIndicator />
          <UpdatePrompt />
        </BrowserRouter>
      </ConnectivityProvider>
    </AuthProvider>
  );
}

// Shows the branded offline overlay when the server is confirmed unreachable.
function ConnectivityGate({ children }) {
  const { status } = useConnectivity();
  return (
    <>
      {children}
      {(status === "offline" || status === "reconnecting") && <OfflineScreen />}
    </>
  );
}
